import {
  useEffect,
  useRef,
  useState
} from "react";

import "./VideoPlayer.css";


// =====================================================
// API BASE
// =====================================================

const API_BASE =
  "https://td9dmwg0-8000.inc1.devtunnels.ms";


// =====================================================
// VIDEO PLAYER
// =====================================================

function VideoPlayer({
  video,
  onClose
}) {

  const videoRef =
    useRef(null);


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  // ===================================================
  // VIDEO URL
  // ===================================================

  const videoUrl =
    `${API_BASE}/video/${video?.id}`;


  // ===================================================
  // RESET PLAYER WHEN VIDEO CHANGES
  // ===================================================

  useEffect(() => {

    if (!video) {
      return;
    }


    setLoading(true);
    setError("");


    const player =
      videoRef.current;


    if (!player) {
      return;
    }


    // Force browser to start loading immediately
    player.load();


  }, [video]);


  // ===================================================
  // VIDEO LOADED
  // ===================================================

  const handleLoadedMetadata =
    () => {

      console.log(
        "Video metadata loaded:",
        video?.name
      );

      setLoading(false);

    };


  // ===================================================
  // VIDEO CAN PLAY
  // ===================================================

  const handleCanPlay =
    () => {

      console.log(
        "Video can play:",
        video?.name
      );

      setLoading(false);

    };


  // ===================================================
  // VIDEO ERROR
  // ===================================================

  const handleVideoError =
    (event) => {

      console.error(
        "Video loading error:",
        event
      );

      setLoading(false);

      setError(
        "Unable to load this video."
      );

    };


  // ===================================================
  // CLOSE WITH ESC
  // ===================================================

  useEffect(() => {

    if (!video) {
      return;
    }


    const handleKeyDown =
      (event) => {

        if (
          event.key ===
          "Escape"
        ) {

          onClose();

        }

      };


    window.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  }, [
    video,
    onClose
  ]);


  // ===================================================
  // NO VIDEO
  // ===================================================

  if (!video) {
    return null;
  }


  // ===================================================
  // RENDER
  // ===================================================

  return (

    <div
      className="video-modal-backdrop"
      onClick={onClose}
    >

      <div
        className="video-modal"
        onClick={(e) =>
          e.stopPropagation()
        }
      >


        {/* ============================================= */}
        {/* HEADER */}
        {/* ============================================= */}

        <div className="video-modal-header">

          <div className="video-info">

            <div className="video-icon">
              ▶
            </div>


            <div className="video-details">

              <div className="video-title">

                {video.name}

              </div>


              <div className="video-subtitle">

                Video preview

              </div>

            </div>

          </div>


          <button
            className="video-close-btn"
            onClick={onClose}
            title="Close"
            type="button"
          >

            ✕

          </button>

        </div>


        {/* ============================================= */}
        {/* VIDEO */}
        {/* ============================================= */}

        <div className="video-container">


          {/* =========================================== */}
          {/* LOADING */}
          {/* =========================================== */}

          {loading && (

            <div className="video-loading">

              <div className="video-loading-spinner" />

              <span>
                Loading video...
              </span>

            </div>

          )}


          {/* =========================================== */}
          {/* ERROR */}
          {/* =========================================== */}

          {error && (

            <div className="video-error">

              <div>
                ⚠
              </div>

              <span>
                {error}
              </span>

              <button
                type="button"
                onClick={() => {

                  setError("");
                  setLoading(true);

                  if (
                    videoRef.current
                  ) {

                    videoRef.current.load();

                  }

                }}
              >

                Retry

              </button>

            </div>

          )}


          {/* =========================================== */}
          {/* VIDEO ELEMENT */}
          {/* =========================================== */}

          <video
            ref={videoRef}

            className="video-element"

            src={videoUrl}

            controls

            autoPlay

            muted

            playsInline

            preload="auto"

            onLoadedMetadata={
              handleLoadedMetadata
            }

            onCanPlay={
              handleCanPlay
            }

            onError={
              handleVideoError
            }
          />

        </div>


        {/* ============================================= */}
        {/* FOOTER */}
        {/* ============================================= */}

        <div className="video-modal-footer">

          <span>
            {video.name}
          </span>


          <span className="video-hint">
            ⛶ Use fullscreen for a larger view
          </span>

        </div>


      </div>

    </div>

  );

}


export default VideoPlayer;